<script setup>
import { computed, ref, watch } from 'vue';
import { useRoadmapsStore } from '@/stores/roadmaps';
import { buildShareUrl } from '@/lib/share';
import { APP_URL } from '@/lib/site';
import { toast } from '@/composables/useDialogs';
import Icon from '@/components/ui/Icon.vue';

const store = useRoadmapsStore();
const roadmap = computed(() => store.byId('demo-q3-platform') || store.sorted[0]);
const url = ref('');
let timer = null;

async function rebuild() {
  if (!roadmap.value) return;
  try {
    url.value = await buildShareUrl(JSON.parse(JSON.stringify(roadmap.value)), APP_URL);
  } catch {
    url.value = '';
  }
}

watch(
  () => [store.revision, store.ready],
  () => {
    clearTimeout(timer);
    timer = setTimeout(rebuild, 250);
  },
  { immediate: true }
);

const token = computed(() => url.value.split('#')[1] || '');

async function copy() {
  try {
    await navigator.clipboard.writeText(url.value);
    toast('Link copied');
  } catch {
    toast('Copy did not work in this browser', { tone: 'error' });
  }
}
</script>

<template>
  <div class="share">
    <div class="card">
      <p class="label">Share link for {{ roadmap?.name }}</p>
      <div class="link" :title="url">
        <span class="host">app.minnimap.com/share#</span><span class="token">{{ token }}</span>
      </div>
      <p class="meta">
        {{ url.length.toLocaleString('en-GB') }} characters. The whole roadmap is inside them, including anything you
        changed in the demo.
      </p>
      <div class="actions">
        <button type="button" class="btn btn--dark" :disabled="!url" @click="copy"><Icon name="copy" :size="14" />Copy link</button>
        <a class="btn" :href="url || APP_URL" target="_blank" rel="noopener"><Icon name="link" :size="14" />Open it in Minni Map</a>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.card {
  padding: 22px;
  border: 1px solid var(--line);
  border-radius: var(--radius-l);
  background: var(--surface);
  box-shadow: 0 24px 48px -28px rgba(23, 20, 42, 0.25);
}

.label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-2);
}

.link {
  position: relative;
  margin-top: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  background: var(--bg);
  font-size: 13px;
  line-height: 1.55;
  word-break: break-all;
  max-height: 116px;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 40px;
    background: linear-gradient(transparent, var(--bg));
  }
}

.host {
  font-weight: 600;
  color: var(--text);
}

.token {
  color: var(--iris-strong);
}

.meta {
  margin-top: 12px;
  font-size: 12px;
  color: var(--text-3);
  line-height: 1.5;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 36px;
  padding: 0 14px;
  border: 1px solid var(--line-strong);
  border-radius: 9px;
  background: var(--surface);
  color: var(--text);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    text-decoration: none;
    border-color: var(--text-3);
  }

  &--dark {
    border-color: var(--ink);
    background: var(--ink);
    color: #fff;

    &:hover {
      background: var(--ink-3);
    }
  }

  &:disabled {
    opacity: 0.5;
  }
}
</style>
